// Copyright ©️ 2026 eVolve MEP, LLC

import { useEvolveGet, type Task, type WithOptional } from '@evolvemep/api-ts';
import { Flex, Loader, Text } from '@mantine/core';

import { EntityStatusBadge } from 'components/Mantine/EntityStatusBadge';
import { TextWithTruncate } from 'components/Mantine/TextWithTruncate';
import { isNotNil } from 'helpers/isNotNil';
import { useTaskStatuses } from 'hooks/typeCacheHooks/useTaskStatuses';

type TaskWithOptionalPredecessors = WithOptional<Pick<Task, 'taskId' | 'taskPredecessors'>, 'taskPredecessors'>;

export const TaskPredecessorsList = ({
  task: { taskId, taskPredecessors },
}: {
  task: TaskWithOptionalPredecessors;
}) => {
  const { states: taskStatusTypes } = useTaskStatuses();
  const { data, loading } = useEvolveGet(`shop/task/${taskId}`, { lazy: isNotNil(taskPredecessors) });
  const predecessors = data?.taskPredecessors ?? taskPredecessors;
  return (
    <>
      {predecessors?.map((p) => (
        <Flex key={p.taskId} justify="space-between" align="center" gap="xl">
          <TextWithTruncate fz="sm">{p.taskName}</TextWithTruncate>
          <EntityStatusBadge
            customStatusEntityName="Task"
            stateName={taskStatusTypes?.find((t) => t.stateId === p.taskStatusTypeId)?.name ?? 'Not Started'}
          />
        </Flex>
      ))}
      {loading ? (
        <Loader type="dots" size="sm" />
      ) : (
        predecessors?.length === 0 && (
          <Text fz="sm" c="dimmed">
            No predecessors.
          </Text>
        )
      )}
    </>
  );
};
